import { useLazyQuery } from '@apollo/client';
import { type Remote, wrap } from 'comlink';
import { useEffect, useRef, useState } from 'react';
import { useSnackBarContext } from '@/contexts/SnackbarContext';
import { REPORT_TYPES } from '../../../../constants';
import {
  SECURE_REPORT_DOWNLOAD_QUERY,
  type SecureReportDownloadQueryData,
} from '../../../../hooks/useReportDownloads';
import type { AssetIdentifier, ReportState } from '../types';
import { finalizeAssetReportHtml, sanitizeAssetReportHtml } from '../utils';
import type { AssetReportSanitizerWorker } from './assetReportSanitizer.worker';

const REPORT_RENDER_ERROR_MESSAGE =
  'This report could not be rendered. Please try downloading the Asset Report to view.';
const REPORT_UNAVAILABLE_MESSAGE = 'This report is not available.';

type UseAssetReportHtmlParams = {
  asset: AssetIdentifier | null;
};

type UseAssetReportHtmlReturn = ReportState & {
  shouldCleanDom: boolean;
  resetCleanDom: () => void;
  loadReport: () => void;
};

const useAssetReportHtml = ({ asset }: UseAssetReportHtmlParams): UseAssetReportHtmlReturn => {
  const { setSnack } = useSnackBarContext();
  const [reportState, setReportState] = useState<ReportState>({
    html: null,
    status: 'idle',
    error: null,
  });
  const [shouldCleanDom, setShouldCleanDom] = useState(false);
  const previousAssetIdRef = useRef<string | null>(null);
  const activeRequestRef = useRef(0);
  const abortControllerRef = useRef<AbortController | null>(null);
  const workerRef = useRef<Worker | null>(null);
  const sanitizerRef = useRef<Remote<AssetReportSanitizerWorker> | null>(null);

  const [getHtmlReport, { loading }] = useLazyQuery<SecureReportDownloadQueryData>(
    SECURE_REPORT_DOWNLOAD_QUERY,
    {
      fetchPolicy: 'cache-and-network',
    },
  );

  useEffect(() => {
    if (typeof window === 'undefined' || typeof Worker === 'undefined') {
      return;
    }

    let isUnmounted = false;

    const createWorkerProduction = async (isUnmounted: boolean) => {
      const sanitizerModule = await import('./assetReportSanitizer.worker');
      if (isUnmounted) {
        return null;
      }
      const WorkerConstructor =
        (sanitizerModule as { default?: new () => Worker }).default ??
        (sanitizerModule as unknown as new () => Worker);

      if (typeof WorkerConstructor !== 'function') {
        return null;
      }
      return new WorkerConstructor();
    };

    const createWorkerDevelopment = () => {
      return new Worker(new URL('./assetReportSanitizer.worker.ts', import.meta.url), {
        type: 'module',
      });
    };

    const initializeWorker = async () => {
      try {
        let worker: Worker | null = null;

        if (process.env.NODE_ENV === 'production') {
          worker = await createWorkerProduction(isUnmounted);
        } else {
          worker = createWorkerDevelopment();
        }

        if (!worker) {
          return;
        }

        if (isUnmounted) {
          worker.terminate();
          return;
        }

        workerRef.current = worker;
        sanitizerRef.current = wrap<AssetReportSanitizerWorker>(worker);
      } catch (_error) {
        workerRef.current = null;
        sanitizerRef.current = null;
      }
    };

    initializeWorker();

    return () => {
      isUnmounted = true;
      workerRef.current?.terminate();
      workerRef.current = null;
      sanitizerRef.current = null;
    };
  }, []);

  useEffect(() => {
    if (asset?.legacyId === previousAssetIdRef.current) {
      return;
    }

    previousAssetIdRef.current = asset?.legacyId ?? null;
    setReportState({
      html: null,
      status: 'idle',
      error: null,
    });

    return () => {
      activeRequestRef.current += 1;
      abortControllerRef.current?.abort();
      abortControllerRef.current = null;
    };
  }, [asset?.legacyId]);

  const sanitizeHtmlReport = async (rawHtml: string): Promise<string> => {
    const sanitizer = sanitizerRef.current;
    if (sanitizer) {
      try {
        const preprocessedHtml = await sanitizer.sanitizeHtml(rawHtml);
        return finalizeAssetReportHtml(preprocessedHtml);
      } catch (_sanitizerError) {
        return sanitizeAssetReportHtml(rawHtml);
      }
    } else {
      return sanitizeAssetReportHtml(rawHtml);
    }
  };

  const loadReport = async () => {
    if (!asset) return;

    const requestId = activeRequestRef.current + 1;
    activeRequestRef.current = requestId;

    abortControllerRef.current?.abort();
    const abortController = new AbortController();
    abortControllerRef.current = abortController;
    const { signal } = abortController;

    if (!asset.isoCode) {
      setReportState({
        html: REPORT_UNAVAILABLE_MESSAGE,
        status: 'unavailable',
        error: null,
      });
      return;
    }

    setReportState(prev => ({
      ...prev,
      status: 'loading',
    }));

    try {
      const { data } = await getHtmlReport({
        variables: {
          assetLegacyId: asset.legacyId,
          isoCode: asset.isoCode,
          reportType: REPORT_TYPES.ENHANCED_HTML_REPORT,
        },
        context: {
          fetchOptions: {
            signal,
          },
        },
      });

      if (signal.aborted || requestId !== activeRequestRef.current) {
        return;
      }

      const downloadUrl = data?.secureReportDownload?.url;
      if (!downloadUrl) {
        setReportState({
          html: REPORT_UNAVAILABLE_MESSAGE,
          status: 'unavailable',
          error: null,
        });
        return;
      }

      const response = await fetch(downloadUrl, { signal });
      if (!response.ok) {
        throw new Error('Unable to load the Asset Report preview.');
      }

      const rawHtml = await response.text();
      if (signal.aborted || requestId !== activeRequestRef.current) {
        return;
      }

      const sanitizedHtml = await sanitizeHtmlReport(rawHtml);

      if (signal.aborted || requestId !== activeRequestRef.current) {
        return;
      }

      setReportState({
        html: sanitizedHtml,
        status: 'success',
        error: null,
      });
      setShouldCleanDom(true);
    } catch (error) {
      if (signal.aborted || requestId !== activeRequestRef.current) {
        return;
      }

      const errorInstance = error instanceof Error ? error : new Error(REPORT_RENDER_ERROR_MESSAGE);

      setReportState({
        html: REPORT_RENDER_ERROR_MESSAGE,
        status: 'error',
        error: errorInstance,
      });

      setSnack({
        open: true,
        severity: 'error',
        message: errorInstance.message,
      });
    } finally {
      if (abortControllerRef.current === abortController) {
        abortControllerRef.current = null;
      }
    }
  };

  const resetCleanDom = (): void => {
    setShouldCleanDom(false);
  };

  return {
    ...reportState,
    status: loading ? 'loading' : reportState.status,
    shouldCleanDom,
    resetCleanDom,
    loadReport,
  };
};

export { useAssetReportHtml, REPORT_RENDER_ERROR_MESSAGE, REPORT_UNAVAILABLE_MESSAGE };
